// Pure helpers for ShareDialog.jsx — no React imports (lib/ convention).
//
// THE CONTRACT GAP THIS FILE USED TO WORK AROUND — NOW CLOSED SERVER-SIDE
// (Task 3.6), FALLBACK KEPT DELIBERATELY. `GET /api/forecasts/:id/shares`
// (Task 3.3) used to return `{lane, label, grantedAt}` for every share, with
// no id on the user lane — `label` (`name || email`) is DISPLAY, not
// IDENTITY. Pre-selecting "already shared with Alice" in the people picker
// (and, critically, PRESERVING that grant across an unrelated edit, since
// the write is a whole-set replace — see `deriveInitialSelection`'s own
// header) then needed an id the endpoint didn't carry, so this file
// recovered identity by matching each people-CANDIDATE's OWN label
// (recomputed with the server's identical `name || email` rule) against the
// share's label.
//
// WHY THAT COULDN'T WAIT FOR "THE ORG GROWS" — IT WAS ALREADY FALSE. The
// label-matching approach doesn't merely degrade at scale (a scarcity
// argument, the kind this whole plan has been eliminating); it fails
// OUTRIGHT, in a small org, TODAY, for a share to anyone no longer in the
// candidate list — leaving the org is a steady-state event, not a growth
// condition. `ShareDialog.test.js`'s own "Departed Colleague" fixture
// demonstrated exactly this before the fix landed. Task 3.6 closed the gap
// at the source: `forecastShares.js`'s GET response now carries a bare
// `userId` on every user-lane entry (not a subject string — `decodeSubject`
// already had the value; this is response-building forwarding a value
// already in hand, not a new decode or query).
//
// `deriveInitialSelection` below therefore checks `s.userId` FIRST, and
// falls through to label-matching only when it's absent — the fallback (and
// `unresolvedUserShares`) is NOT dead code kept out of caution: it is what
// stops an old cached response, or some other future response shape that
// happens to omit the field, from turning into a SILENT drop instead of the
// loud refusal below. The "two Sams" ambiguity this fallback still guards
// against (a label matching zero or more than one candidate) is a
// genuinely rarer case now — most shares resolve by id — but the SAME
// fail-safe design still applies to whatever's left: `deriveInitialSelection`
// surfaces every share it can't resolve via `unresolvedUserShares`, and
// `ShareDialog.jsx` BLOCKS saving while that list is non-empty, rather than
// silently dropping a grant the owner never touched.

/**
 * Recompute a people-candidate's display label with the SAME rule
 * `forecastShares.js`'s response building uses (`name || email`) — kept as
 * its own function so the one rule has one definition, not a copy risking
 * drift from the server's.
 *
 * @param {{name?: string|null, email?: string}} candidate
 * @returns {string|null}
 */
export function candidateLabel(candidate) {
  return (candidate && (candidate.name || candidate.email)) || null;
}

/**
 * From a loaded `GET /api/forecasts/:id/shares` response and the share
 * picker's people candidates, derive the dialog's initial selection state.
 *
 * TEAM AND ORG NEED NO MATCHING AT ALL — stated here because it is the
 * asymmetry that makes the user lane's problem look, at a glance, like it
 * should generalize when it does not. A team share's `label` IS its slug
 * (forecastShares.js's response building uses the slug directly, no
 * lookup), so `selectedTeamSlugs` is read straight off `shares` with no
 * candidate list involved. An org share names no further identity at all —
 * there is exactly one org a caller can ever grant to (their own, at write
 * time) — so `orgSelected` is a plain boolean, no id needed either.
 *
 * NO JOIN AGAINST THE TEAM LIST, ONLY THE PEOPLE LIST — this is the
 * boundary that keeps this file from rebuilding the roster leak 3.4 closed.
 * Matching a share's label against `peopleCandidates` (bare identity: id,
 * name, email) recovers WHO a grant names. Matching it against `teams`
 * would answer a different, forbidden question ("is this person on team
 * X"), which this function never asks — `teams` is not even a parameter.
 *
 * @param {object} args
 * @param {Array<{lane: string, label: string|null}>} args.shares the loaded GET response's `shares`
 * @param {Array<{userId: string, name?: string|null, email?: string}>} args.peopleCandidates
 * @returns {{
 *   orgSelected: boolean,
 *   selectedTeamSlugs: Set<string>,
 *   selectedPeopleIds: Set<string>,
 *   unresolvedUserShares: Array<{lane: "user", label: string|null}>,
 * }}
 */
export function deriveInitialSelection({ shares, peopleCandidates }) {
  const rows = Array.isArray(shares) ? shares : [];
  const candidates = Array.isArray(peopleCandidates) ? peopleCandidates : [];

  const orgSelected = rows.some((s) => s?.lane === "org");
  // The URL box's state. One grant, so presence is the whole answer — there is
  // nothing to resolve and nothing that can go orphaned.
  const linkSelected = rows.some((s) => s?.lane === "link");
  const selectedTeamSlugs = new Set(
    rows.filter((s) => s?.lane === "team" && typeof s.label === "string").map((s) => s.label),
  );

  const selectedPeopleIds = new Set();
  const unresolvedUserShares = [];
  for (const s of rows) {
    if (s?.lane !== "user") continue;
    // Task 3.6 — the server now sends `userId` directly (forecastShares.js's
    // GET response), so identity no longer needs recovering FROM the label
    // at all in the common case. Checked first, ahead of the label-matching
    // fallback below: label-matching doesn't merely degrade as an org grows
    // (a scarcity argument), it fails OUTRIGHT, today, for a share to anyone
    // no longer in the candidate list — leaving the fallback in place (never
    // deleting it) is what keeps an old cached response, or a future
    // response missing the field for some other reason, from turning into a
    // silent drop instead of the loud refusal below.
    if (s.userId) {
      selectedPeopleIds.add(s.userId);
      continue;
    }
    const matches = candidates.filter((c) => candidateLabel(c) === s.label);
    if (matches.length === 1) {
      selectedPeopleIds.add(matches[0].userId);
    } else {
      // Zero matches (the person is no longer a candidate — e.g. they left
      // the org) OR more than one (an unresolvable label collision) both
      // land here: this function does not distinguish WHY it can't resolve
      // one, only THAT it can't, which is all `unresolvedUserShares.length
      // > 0` needs to know to refuse a save.
      unresolvedUserShares.push(s);
    }
  }

  return { orgSelected, linkSelected, selectedTeamSlugs, selectedPeopleIds, unresolvedUserShares };
}

/**
 * Build the `PUT` request body's `shares` array from the dialog's current
 * selection — the WHOLE desired set, across all three lanes, in the exact
 * shape `forecastShares.js`'s `_validateShares` expects. No subject strings
 * anywhere (task-3.5-checklist.md §2): every entry is a bare `orgId`/`slug`/
 * `userId`, constructed server-side into a subject only after this request
 * lands.
 *
 * @param {object} args
 * @param {boolean} args.orgSelected
 * @param {boolean} [args.linkSelected] whether the URL box is ticked
 * @param {string|null} args.orgId the CALLER's own org id (from `/api/me`) —
 *   never a value read off a loaded share (see module header: an org share
 *   carries no id on `GET`, and needs none, because there is only ever one
 *   org a caller can grant to).
 * @param {Set<string>} args.selectedTeamSlugs
 * @param {Set<string>} args.selectedPeopleIds
 * @returns {Array<{type: "org", orgId: string} | {type: "team", slug: string} | {type: "user", userId: string}>}
 */
export function buildDesiredShares({
  orgSelected,
  linkSelected,
  orgId,
  selectedTeamSlugs,
  selectedPeopleIds,
}) {
  const desired = [];
  if (orgSelected && orgId) {
    desired.push({ type: "org", orgId });
  }
  // No payload, and no `orgId` guard either: the link audience is every signed-in
  // user, so it is the one lane an owner with NO organization can still use.
  if (linkSelected) {
    desired.push({ type: "link" });
  }
  for (const slug of selectedTeamSlugs) {
    desired.push({ type: "team", slug });
  }
  for (const userId of selectedPeopleIds) {
    desired.push({ type: "user", userId });
  }
  return desired;
}

/**
 * Whether to offer the SHARE affordance for the active forecast.
 *
 * Extracted from `App.jsx` rather than left inline because a condition
 * inside that render tree is unreachable to every test the repo has: the
 * app vitest project has no jsdom, and the Playwright fixtures seed no
 * shared rows, so the viewer case had no coverage at all. Both dialog bugs
 * found in live testing were the same shape — a decision expressed only in
 * JSX — so this one is written where it can be asserted.
 *
 * OWNERSHIP IS NO LONGER PART OF THIS GATE. It used to hide SHARE entirely from
 * a viewer. A recipient who wants to pass a forecast on has a real question, and
 * an absent button answers it with silence — they cannot tell whether sharing is
 * unavailable, broken, or simply not theirs. So the button is offered to
 * everyone and the DIALOG answers, in a sentence, that only the owner manages
 * sharing (`ShareDialog`'s `isOwn === false` branch). Deliberately prose rather
 * than a disabled picker: a greyed list would disclose who else has access, which
 * a viewer must not learn.
 *
 * @param {object} args
 * @param {boolean} args.shareDialogAvailable the `chatboard-org-sharing` flag
 * @param {string|null|undefined} args.forecastId persisted id — an unsaved run
 *   has no server row to share (same rule copy links use)
 * @returns {boolean}
 */
export function canOfferSharing({ shareDialogAvailable, forecastId }) {
  return Boolean(shareDialogAvailable) && Boolean(forecastId);
}

/**
 * Grants that have NO checkbox in the picker — a team whose members have all
 * since left it, or a person no longer in the org. `forecast_shares` still holds
 * the row, but the candidate lists only offer teams with a current member and
 * people currently in the org, so nothing on screen represents these.
 *
 * WHY THEY ARE NOW RENDERED RATHER THAN PRESERVED INVISIBLY. The dialog used to
 * fold orphans silently back into every save, because an untouched control could
 * not express intent about a lane it did not draw. The additive-checkbox model
 * replaces that with a single legible rule — **a checked box means access, an
 * unchecked box means none** — and invisible preservation contradicts it
 * directly: the owner would uncheck everything, save, and still be sharing. So
 * an orphan gets a real checkbox, checked, labelled for what it is. The owner can
 * see it and decide. Nothing is dropped silently and nothing is kept silently.
 *
 * @param {object} args
 * @param {Array<{lane?: string, label?: string, userId?: string}>} args.shares loaded grants
 * @param {Array<{slug?: string}>} args.teamCandidates teams the picker offers
 * @param {Array<{userId?: string}>} args.peopleCandidates people the picker offers
 * @returns {{teams: Array<{slug: string}>, people: Array<{userId: string, label: string}>}}
 */
export function orphanGrants({ shares, teamCandidates, peopleCandidates }) {
  const rows = Array.isArray(shares) ? shares : [];
  const teamSlugs = new Set((teamCandidates || []).map((t) => t?.slug).filter(Boolean));
  const peopleIds = new Set((peopleCandidates || []).map((c) => c?.userId).filter(Boolean));
  const teams = [];
  const people = [];
  for (const share of rows) {
    if (share?.lane === "team" && typeof share.label === "string" && !teamSlugs.has(share.label)) {
      teams.push({ slug: share.label });
    }
    // A user grant is an orphan only when the server told us WHO it is (userId)
    // and that person is not a candidate. Without a userId there is nothing to
    // submit back, which `deriveInitialSelection` already refuses a save over —
    // that louder failure is the right one, so do not quietly render it here.
    if (share?.lane === "user" && share.userId && !peopleIds.has(share.userId)) {
      people.push({ userId: share.userId, label: share.label || share.userId });
    }
  }
  return { teams, people };
}

/**
 * Case-insensitive substring match for the picker's search field.
 *
 * Matches on the RENDERED label, which is what the user is reading and typing
 * against — matching an id or a raw email that the row does not display would
 * make results appear for a query the list cannot explain.
 *
 * @param {string} label the row's visible text
 * @param {string} query the current search input
 * @returns {boolean} true when the row should be shown (an empty query shows all)
 */
export function matchesQuery(label, query) {
  const q = String(query ?? "")
    .trim()
    .toLowerCase();
  if (!q) return true;
  return String(label ?? "")
    .toLowerCase()
    .includes(q);
}

/**
 * Copy the forecast URL, reporting the outcome as a state the UI can render.
 *
 * Extracted from the dialog because it is the one part of the URL box with real
 * behaviour — and the dialog's test harness renders markup to a string, so a
 * handler living inside the component cannot be exercised at all (that file's own
 * header says so). Here it is a plain function with an injected writer.
 *
 * "failed" IS A RENDERED STATE, not a swallowed error. A refused clipboard —
 * permissions, a non-secure context, a browser that simply says no — must say so
 * and show the URL, because the whole point of the flow is walking away with it.
 * Quietly claiming a copy that did not happen is the one outcome worse than
 * failing.
 *
 * @param {string|null|undefined} url the forecast URL, or falsy for nothing to do
 * @param {(text: string) => (boolean|Promise<boolean>)} [writeClipboard] injected
 *   writer; must answer `true` on success. Same contract as `useMintedLinkStash`.
 * @returns {Promise<"copied"|"failed"|null>} null when there was no URL to copy
 */
export async function copyForecastUrl(url, writeClipboard) {
  if (!url) return null;
  const write = writeClipboard || ((text) => navigator.clipboard.writeText(text).then(() => true));
  try {
    // Strict `=== true`: a writer that resolves undefined (the shape
    // `navigator.clipboard.writeText` itself has) has not confirmed anything.
    return (await write(url)) === true ? "copied" : "failed";
  } catch {
    return "failed";
  }
}

/**
 * The one- or two-letter avatar for a person row: first + last initial of a name,
 * else the first letter of an email, else "?". Display only.
 *
 * @param {string|null|undefined} label a name or an email
 * @returns {string}
 */
export function initialsOf(label) {
  const s = String(label ?? "").trim();
  if (!s) return "?";
  if (s.includes("@") && !s.includes(" ")) return s[0].toUpperCase();
  const words = s.split(/\s+/).filter(Boolean);
  const first = words[0][0] || "";
  const last = words.length > 1 ? words[words.length - 1][0] : "";
  return (first + last).toUpperCase() || "?";
}
